import React, { useState, useRef, useEffect } from 'react';
import profileImg from '../assets/img/pfp.jpeg';
import config from '../config';

function getVisitorId() {
  const key = 'visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = 'v_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
}

// Matches markdown links [label](url) and bare URLs.
const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"')\]},;]+)/g;

export function linkify(text) {
  const nodes = [];
  let last = 0;
  for (const m of text.matchAll(LINK_PATTERN)) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const href = m[2] || m[3];
    const label = m[1] || href;
    nodes.push(
      <a key={m.index} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
    );
    last = m.index + m[0].length;
  }
  if (nodes.length === 0) return text;
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const INACTIVITY_MS = 15 * 1000; // TODO: change back to 3 * 60 * 1000 after testing

function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const visitorId = useRef(getVisitorId());
  const inactivityTimer = useRef(null);
  const notifiedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inactivity timer: notify after 3 min of no new messages
  useEffect(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

    const hasQA = messages.some(m => m.role === 'user') && messages.some(m => m.role === 'assistant');
    if (!hasQA) return;

    // Reset notified flag if user sent a new message after previous notification
    if (notifiedRef.current && messages[messages.length - 1]?.role === 'assistant') {
      // New assistant reply after user sent another message — allow re-notify
    }

    inactivityTimer.current = setTimeout(() => {
      if (notifiedRef.current) return;
      notifiedRef.current = true;
      fetch('/api/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Client': config.deployment.clientHeader,
        },
        body: JSON.stringify({ messages, visitorId: visitorId.current }),
      }).catch(() => {});
    }, INACTIVITY_MS);

    return () => clearTimeout(inactivityTimer.current);
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [input]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    notifiedRef.current = false;
    const userMessage = { role: 'user', content: trimmed };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Client': config.deployment.clientHeader,
          'X-Visitor-ID': visitorId.current,
        },
        body: JSON.stringify({ messages: newMessages, localHour: new Date().getHours() }),
      });

      const data = await res.json();

      if (data.error) {
        setMessages([...newMessages, { role: 'assistant', content: data.error }]);
      } else if (data.message) {
        setMessages([...newMessages, { role: 'assistant', content: data.message }]);
      }
    } catch (err) {
      setMessages([
        ...newMessages,
        { role: 'assistant', content: "hmm, something went wrong. try again?" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePromptClick = (prompt) => {
    setInput(prompt);
    setTimeout(() => {
      const syntheticMsg = { role: 'user', content: prompt };
      const newMessages = [syntheticMsg];
      setMessages(newMessages);
      setInput('');
      setIsLoading(true);

      fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Chat-Client': config.deployment.clientHeader,
          'X-Visitor-ID': visitorId.current,
        },
        body: JSON.stringify({ messages: newMessages, localHour: new Date().getHours() }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.error) {
            setMessages([...newMessages, { role: 'assistant', content: data.error }]);
          } else if (data.message) {
            setMessages([...newMessages, { role: 'assistant', content: data.message }]);
          }
        })
        .catch(() => {
          setMessages([...newMessages, { role: 'assistant', content: "hmm, something went wrong. try again?" }]);
        })
        .finally(() => setIsLoading(false));
    }, 0);
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="chat-page">
      {!hasMessages ? (
        <div className="chat-empty">
          <p className="chat-greeting">{config.chat.greeting}</p>
          <div className="suggested-prompts">
            {config.chat.suggestedPrompts.map((prompt, i) => (
              <button key={i} className="suggested-prompt" onClick={() => handlePromptClick(prompt)}>
                {prompt}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="chat-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-message ${msg.role}`}>
              {msg.role === 'assistant' && (
                <img src={profileImg} alt={config.name} className="message-avatar" />
              )}
              <div className="message-content">
                <p>{linkify(msg.content)}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="chat-message assistant">
              <img src={profileImg} alt={config.name} className="message-avatar" />
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      <div className={`chat-input-area ${hasMessages ? 'has-messages' : ''}`}>
        <div className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            className="chat-input"
            placeholder={config.chat.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={`chat-send ${input.trim() ? 'active' : ''}`}
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 12V4M8 4L4 8M8 4L12 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Chat;
